import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { Form, Input, Button, Alert, Modal, Checkbox, Select } from 'antd'
import PageLoader from '../util/PageLoader'
import { ReactComponent as Logo } from '../../assets/logo.svg'

const validateValue = (field, raw) => {
  // A checkbox always has a definite Yes/No answer, so it never errors.
  if (field.type === 'checkbox') return null
  const v = raw == null ? '' : String(raw).trim()
  if (!v) return field.required ? 'Required' : null
  switch (field.type) {
    case 'dropdown':
      if (Array.isArray(field.options) && !field.options.includes(v)) return 'Invalid selection'
      return null
    case 'email':
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Invalid email'
      return null
    case 'vpsAccount':
      // Strip dots/spaces/other separators before checking — the approved-list
      // check (server-side) compares the bare 12 digits.
      if (!/^\d{12}$/.test(v.replace(/\D+/g, ''))) return 'VPS account must be 12 digits'
      return null
    case 'phone':
      if (!/^\d{8}$/.test(v.replace(/\s+/g, ''))) return 'Phone must be 8 digits'
      return null
    case 'number':
      if (!/^-?\d+(\.\d+)?$/.test(v)) return 'Must be a number'
      return null
    default:
      return null
  }
}

const Fill = () => {
  const { hash } = useParams()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [form, setForm] = useState(null)
  const [values, setValues] = useState({})
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    axios.get(`${process.env.REACT_APP_API}/api/sign/${hash}/form`)
      .then(r => {
        if (cancelled) return
        setForm(r.data)
        setLoading(false)
      })
      .catch(err => {
        if (cancelled) return
        const msg = err.response && err.response.data && err.response.data.message
        setLoadError(msg || 'Could not load form')
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [hash])

  const setField = (key, val) => {
    setValues(v => ({ ...v, [key]: val }))
    setErrors(e => ({ ...e, [key]: undefined }))
  }

  const postSubmit = async (confirmDuplicate) => {
    setSubmitting(true)
    try {
      const r = await axios.post(`${process.env.REACT_APP_API}/api/sign/${hash}/form`, { values, confirmDuplicate })
      if (r.data && r.data.duplicate) {
        setSubmitting(false)
        Modal.confirm({
          title: 'Already signed',
          content: r.data.message || 'A signature with this VPS account and number of shares has already been registered. Do you want to sign again?',
          okText: 'Sign again',
          cancelText: 'Cancel',
          onOk: () => postSubmit(true)
        })
        return
      }
      window.location.href = r.data.signingUrl
    } catch (err) {
      setSubmitting(false)
      if (err.response && err.response.data && err.response.data.errors) {
        setErrors(err.response.data.errors)
      } else {
        setLoadError('Could not start signing. Please try again.')
      }
    }
  }

  const submit = () => {
    const next = {}
    for (const f of form.fields) {
      const err = validateValue(f, values[f.key])
      if (err) next[f.key] = err
    }
    setErrors(next)
    if (Object.keys(next).length > 0) return

    postSubmit(false)
  }

  const renderControl = (f) => {
    if (f.type === 'checkbox') {
      return (
        <Checkbox
          checked={values[f.key] === 'Yes'}
          onChange={(e) => setField(f.key, e.target.checked ? 'Yes' : 'No')}
          disabled={submitting}
        >
          Yes
        </Checkbox>
      )
    }
    if (f.type === 'dropdown') {
      return (
        <Select
          value={values[f.key] || undefined}
          onChange={(v) => setField(f.key, v)}
          disabled={submitting}
          size="large"
          allowClear
          placeholder="Select…"
          options={(f.options || []).map(o => ({ label: o, value: o }))}
        />
      )
    }
    return (
      <Input
        type={f.inputType || 'text'}
        value={values[f.key] || ''}
        onChange={(e) => setField(f.key, e.target.value)}
        disabled={submitting}
        size="large"
      />
    )
  }

  const renderShell = (content) => (
    <div className="brandShell">
      <header className="brandHeader">
        <Logo aria-label="Arctic" />
      </header>
      <main className="brandMain">
        <div className="brandCard">
          {content}
        </div>
      </main>
      <footer className="brandFooter">
        Arctic Securities AS
      </footer>
    </div>
  )

  if (loading) return renderShell(<PageLoader />)
  if (loadError) return renderShell(<Alert type="error" message={loadError} showIcon />)

  const title = (form.formTitle && form.formTitle.trim()) || form.documentName
  const introHtml = form.formIntro && form.formIntro.trim()

  return renderShell(
    <>
      <h1>{title}</h1>
      {introHtml ? (
        <div className="brandCard__lead brandCard__richLead" dangerouslySetInnerHTML={{ __html: introHtml }} />
      ) : (
        <p className="brandCard__lead">
          Please confirm the details below before signing. Values shown here will be prefilled in the document; you can still edit them in the signing view.
        </p>
      )}
      <Form layout="vertical" onFinish={submit}>
        {form.fields.map(f => (
          <Form.Item
            key={f.key}
            label={f.label + (f.required ? ' *' : '')}
            validateStatus={errors[f.key] ? 'error' : ''}
            help={errors[f.key]}
          >
            {renderControl(f)}
          </Form.Item>
        ))}
        <Button type="primary" htmlType="submit" loading={submitting} block>
          Continue to signing
        </Button>
      </Form>
    </>
  )
}

export default Fill
